import { Box, Menu, Stack } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import * as React from "react";
import { useScheduleTimeRange } from "../stores/useScheduleTimeRange";
import { snapToQuarterHour } from "../utils/timeUtils";
import { TimeRangeSlider } from "./TimeRangeSlider";

interface TimeControlBarProps {
  currentTime: Date;
  onTimeChange: (newTime: Date) => void;
  tripCountsByDate?: Map<string, number>;
  // Props for trip duration display
  selectedTripStart?: Date;
  selectedTripEnd?: Date;
  showTripDuration?: boolean;
  selectedTailNumber?: string;
  // Props for jump to trip buttons
  onJumpToNextTrip?: () => void;
  onJumpToPreviousTrip?: () => void;
  showTripJumpButtons?: boolean;
  hasNextTrip?: boolean;
  hasPreviousTrip?: boolean;
  // Props for View Schedule button
  onViewSchedule?: () => void;
  showViewScheduleButton?: boolean;
  viewScheduleDisabled?: boolean;
}

export const TimeControlBar: React.FC<TimeControlBarProps> = ({
  currentTime,
  onTimeChange,
  tripCountsByDate,
  selectedTripStart,
  selectedTripEnd,
  showTripDuration = false,
  selectedTailNumber,
  onJumpToNextTrip,
  onJumpToPreviousTrip,
  showTripJumpButtons = false,
  hasNextTrip = false,
  hasPreviousTrip = false,
  onViewSchedule,
  showViewScheduleButton = false,
  viewScheduleDisabled = false,
}) => {
  // Use Zustand store for time range
  const { rangeStart, rangeEnd, setTimeRange, getDefaultWeekRange } =
    useScheduleTimeRange();

  // Update range when current time changes significantly (e.g., jumped to a different day)
  React.useEffect(() => {
    // Only update range if current time is truly outside the range
    // Allow currentTime to be exactly at the boundaries without triggering a range update
    const isCurrentTimeOutsideRange =
      currentTime.getTime() < rangeStart.getTime() - 1000 ||
      currentTime.getTime() > rangeEnd.getTime() + 1000;

    if (isCurrentTimeOutsideRange) {
      const newRange = getDefaultWeekRange(currentTime);
      // Only update if the range would actually change
      if (
        newRange.start.getTime() !== rangeStart.getTime() ||
        newRange.end.getTime() !== rangeEnd.getTime()
      ) {
        setTimeRange(newRange.start, newRange.end);
      }
    }
  }, [currentTime, rangeStart, rangeEnd, setTimeRange, getDefaultWeekRange]);

  const handleRangeChange = (start: Date, end: Date) => {
    setTimeRange(start, end);
  };

  // Calendar menu state
  const [calendarAnchor, setCalendarAnchor] =
    React.useState<null | HTMLElement>(null);
  const calendarOpen = Boolean(calendarAnchor);

  const handleCalendarClose = () => {
    setCalendarAnchor(null);
  };

  const handleDateSelect = (date: Date | null) => {
    if (date) {
      onTimeChange(snapToQuarterHour(date));
      handleCalendarClose();
    }
  };

  return (
    <Stack
      spacing={0}
      sx={{
        ml: 4,
        mr: 6,
        maxHeight: "80px", // Prevent excessive height expansion
        overflow: "visible", // Allow dropdowns to overflow
      }}
    >
      {/* Compact controls row - optimized for iPad */}
      <Stack
        direction="row"
        spacing={{ xs: 1, sm: 2 }}
        alignItems="center"
        sx={{
          padding: { xs: 0.5, sm: 1 },
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Action buttons grouped together - removed Add Obstacle as it's now in Add Aircraft dialog */}
        <Stack direction="row" spacing={1} alignItems="center"></Stack>
      </Stack>

      {/* Time range slider with inline date/time controls */}
      <TimeRangeSlider
        currentTime={currentTime}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        onTimeChange={onTimeChange}
        onRangeChange={handleRangeChange}
        tripCountsByDate={tripCountsByDate}
        showDateTimePicker={true}
        showNowButton={true}
        selectedTripStart={selectedTripStart}
        selectedTripEnd={selectedTripEnd}
        showTripDuration={showTripDuration}
        selectedTailNumber={selectedTailNumber}
        onJumpToNextTrip={onJumpToNextTrip}
        onJumpToPreviousTrip={onJumpToPreviousTrip}
        showTripJumpButtons={showTripJumpButtons}
        hasNextTrip={hasNextTrip}
        hasPreviousTrip={hasPreviousTrip}
        onViewSchedule={onViewSchedule}
        showViewScheduleButton={showViewScheduleButton}
        viewScheduleDisabled={viewScheduleDisabled}
      />

      {/* Calendar Picker */}
      <Menu
        anchorEl={calendarAnchor}
        open={calendarOpen}
        onClose={handleCalendarClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            "& .MuiPickersCalendarHeader-root": {
              paddingLeft: 2,
              paddingRight: 2,
            },
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <StaticDatePicker
            displayStaticWrapperAs="desktop"
            value={currentTime}
            onChange={handleDateSelect}
            slotProps={{
              actionBar: {
                actions: ["today"],
              },
            }}
          />
        </LocalizationProvider>
      </Menu>
    </Stack>
  );
};
